import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import {
  IconArrowDown,
  IconArrowUp,
  IconArrowsSort,
  IconSortAscendingLetters,
  IconSortDescendingLetters,
  IconSortAZ,
  IconFilter,
  IconFilterX,
  IconGripVertical,
  IconSearch,
} from '@tabler/icons-react';
import { WP_ICON_DATATABLE_TH_ACTION } from '../../icons/constants.js';
import { cx } from '../../utils/cx.js';
import { DataTableRowSelectControl } from './DataTableRowSelectControl.js';
import { columnHeaderAriaLabel, columnHeaderHasLabel } from './dataTableColumns.js';
import { getDataTableCellProps, wpPinToCellPin, type DataTableDensity } from './dataTableCell.js';
import type { Column, SortDirection } from './types.js';
import styles from './DataTable.module.css';
import { Pressable } from '../../primitives/Pressable.js';

export function DataTableColumnHeader<T>({
  column,
  actionColumn,
  compactAction,
  stickySide,
  columnWidthStyle,
  resizable,
  onResizePointerDown,
  onResizeDoubleClick,
  resizing,
  pageSelection,
}: {
  column: Column<T>;
  actionColumn?: boolean;
  compactAction?: boolean;
  stickySide?: 'start' | 'end';
  columnWidthStyle?: CSSProperties;
  resizable?: boolean;
  onResizePointerDown?: (e: React.PointerEvent<HTMLButtonElement>) => void;
  onResizeDoubleClick?: () => void;
  resizing?: boolean;
  pageSelection?: {
    checked: boolean;
    indeterminate?: boolean;
    disabled?: boolean;
    onChange: () => void;
  };
}) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  const hasFilter = Boolean(column.headerFilter);
  const filterKind = column.headerFilterKind === 'search' ? 'search' : 'filter';
  const filterActive = Boolean(column.headerFilterActive);

  const updatePanelPos = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(320, Math.max(220, window.innerWidth - 16));
    let left = r.right - width;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    const top = r.bottom + 6;
    setPanelPos({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    updatePanelPos();
  }, [open, updatePanelPos]);

  useEffect(() => {
    if (!open || !panelPos || !panelRef.current) {
      wasOpenRef.current = open;
      return;
    }
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;
    const root = panelRef.current;
    const focusable = root.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])',
    );
    if (!focusable) return;
    const id = window.requestAnimationFrame(() => {
      focusable.focus();
      if (focusable instanceof HTMLInputElement && filterKind === 'search') {
        const len = focusable.value.length;
        try {
          focusable.setSelectionRange(len, len);
        } catch {
          /* non-text inputs */
        }
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [open, panelPos, filterKind]);

  useLayoutEffect(() => {
    if (!open) return;
    const onMove = () => updatePanelPos();
    window.addEventListener('resize', onMove);
    document.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [open, updatePanelPos]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const showBadge =
    filterActive &&
    column.headerFilterBadge != null &&
    column.headerFilterBadge !== false &&
    column.headerFilterBadge !== '';
  const hasResetSlot = Boolean(column.onHeaderFilterReset);
  const canReset = filterActive && hasResetSlot;
  const ariaName = columnHeaderAriaLabel(column);
  const sortable = Boolean(column.sortable);
  const sortDir = column.sortDirection ?? null;
  const sortKind = column.sortKind ?? 'default';
  const sortAlpha = sortKind === 'alpha';

  const filterPanel =
    open && panelPos && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            className={styles.filterPanel}
            role="dialog"
            aria-label={
              ariaName
                ? filterKind === 'search'
                  ? `Recherche ${ariaName}`
                  : `Filtre ${ariaName}`
                : filterKind === 'search'
                  ? 'Recherche'
                  : 'Filtre'
            }
            style={{ top: panelPos.top, left: panelPos.left, width: panelPos.width }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={styles.thFilter}>{column.headerFilter}</div>
          </div>,
          document.body,
        )
      : null;

  const headerDensity: DataTableDensity = compactAction ? 'compact' : 'default';

  if (actionColumn && !hasFilter) {
    const actionHeader = getDataTableCellProps({
      element: 'th',
      part: 'actions',
      pin: wpPinToCellPin(stickySide),
      density: headerDensity,
    });
    return (
      <th {...actionHeader} aria-label="Actions" style={{ ...columnWidthStyle, ...actionHeader.style }}>
        <span className={styles.thLabelSpacer} aria-hidden />
      </th>
    );
  }

  const headerCell = getDataTableCellProps({
    element: 'th',
    part: actionColumn ? 'actions' : 'header',
    pin: wpPinToCellPin(stickySide),
    density: actionColumn ? headerDensity : undefined,
    extraClassName: cx(
      open && styles.thFilterOpen,
      hasFilter && filterActive && styles.thFilterActive,
      resizable && styles.thResizable,
      resizing && styles.thResizing,
      column.columnClassName,
      actionColumn && styles.thAction,
    ),
  });

  return (
    <th
      {...headerCell}
      className={cx(headerCell.className, pageSelection && styles.thHasPageSelect)}
      style={{ ...columnWidthStyle, ...(column.columnStyle ?? {}) }}
    >
      {pageSelection ? (
        <DataTableRowSelectControl
          variant="header"
          checked={pageSelection.checked}
          indeterminate={pageSelection.indeterminate}
          disabled={pageSelection.disabled}
          ariaLabel="Sélectionner toute la page"
          onChange={pageSelection.onChange}
        />
      ) : null}
      <div className={styles.thInner}>
        {sortable || hasFilter ? (
          <div
            className={cx(
              styles.thActionsRow,
              column.headerActionsAlign === 'start' && styles.thActionsRowStart,
            )}
          >
            {sortable ? (
              <Pressable
                type="button"
                className={cx(styles.sortBtn, sortDir && styles.sortBtnActive)}
                aria-label={
                  sortAlpha
                    ? sortDir === 'asc'
                      ? `Trié A → Z : ${ariaName}`
                      : sortDir === 'desc'
                        ? `Trié Z → A : ${ariaName}`
                        : `Trier alphabetiquement : ${ariaName}`
                    : sortDir === 'asc'
                      ? `Trié croissant : ${ariaName}`
                      : sortDir === 'desc'
                        ? `Trié décroissant : ${ariaName}`
                        : `Trier : ${ariaName}`
                }
                onClick={() => {
                  const next: SortDirection = sortDir === 'asc' ? 'desc' : 'asc';
                  column.onSort?.(next);
                }}
              >
                {sortAlpha ? (
                  sortDir === 'asc' ? (
                    <IconSortAscendingLetters {...WP_ICON_DATATABLE_TH_ACTION} aria-hidden />
                  ) : sortDir === 'desc' ? (
                    <IconSortDescendingLetters {...WP_ICON_DATATABLE_TH_ACTION} aria-hidden />
                  ) : (
                    <IconSortAZ {...WP_ICON_DATATABLE_TH_ACTION} aria-hidden />
                  )
                ) : sortDir === 'asc' ? (
                  <IconArrowUp {...WP_ICON_DATATABLE_TH_ACTION} aria-hidden />
                ) : sortDir === 'desc' ? (
                  <IconArrowDown {...WP_ICON_DATATABLE_TH_ACTION} aria-hidden />
                ) : (
                  <IconArrowsSort {...WP_ICON_DATATABLE_TH_ACTION} aria-hidden />
                )}
              </Pressable>
            ) : null}
            {hasFilter ? (
              <div className={styles.filterAnchor} ref={anchorRef}>
                {showBadge ? (
                  <span
                    className={styles.filterBadge}
                    aria-label={
                      filterKind === 'search' ? `${ariaName} : recherche active` : `${ariaName} : filtre actif`
                    }
                  >
                    {column.headerFilterBadge}
                  </span>
                ) : null}
                {hasResetSlot ? (
                  <Pressable
                    type="button"
                    className={cx(styles.filterIconBtn, styles.filterResetBtn, !canReset && styles.filterResetHidden)}
                    aria-label={
                      column.headerFilterResetAriaLabel ??
                      (filterKind === 'search'
                        ? `Réinitialiser la recherche : ${ariaName}`
                        : `Réinitialiser filtre : ${ariaName}`)
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canReset) return;
                      column.onHeaderFilterReset?.();
                      setOpen(false);
                    }}
                  >
                    <IconFilterX {...WP_ICON_DATATABLE_TH_ACTION} aria-hidden />
                  </Pressable>
                ) : null}
                <Pressable
                  type="button"
                  className={styles.filterIconBtn}
                  aria-expanded={open}
                  aria-label={
                    ariaName
                      ? filterKind === 'search'
                        ? `Rechercher : ${ariaName}`
                        : `Filtrer : ${ariaName}`
                      : filterKind === 'search'
                        ? 'Rechercher dans la colonne'
                        : 'Filtrer la colonne'
                  }
                  aria-haspopup="dialog"
                  onClick={() => setOpen((v) => !v)}
                >
                  {filterKind === 'search' ? (
                    <IconSearch {...WP_ICON_DATATABLE_TH_ACTION} aria-hidden />
                  ) : (
                    <IconFilter {...WP_ICON_DATATABLE_TH_ACTION} aria-hidden />
                  )}
                </Pressable>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className={styles.thLabelRow}>
          {columnHeaderHasLabel(column) ? (
            <span className={cx(styles.thLabel, column.headerNoWrap && styles.thLabelNowrap)}>
              {column.label}
            </span>
          ) : (
            <span className={styles.thLabelSpacer} aria-hidden />
          )}
        </div>
      </div>
      {filterPanel}
      {resizable ? (
        <Pressable
          type="button"
          tabIndex={-1}
          aria-label={`Redimensionner la colonne ${ariaName}`}
          title="Glisser pour redimensionner · double-clic pour réinitialiser"
          className={styles.colResizeHandle}
          data-datatable-col-resize
          onPointerDown={onResizePointerDown}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onResizeDoubleClick?.();
          }}
        >
          <IconGripVertical size={12} stroke={1.75} aria-hidden />
        </Pressable>
      ) : null}
    </th>
  );
}
